import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Drop, Plant } from "@phosphor-icons/react";

export default function Nutrition() {
  const today = new Date().toISOString().slice(0, 10);
  const [date, setDate] = useState(today);
  const [entry, setEntry] = useState({ water_glasses: 0, supplements_taken: [], meals: [], weight_kg: "", notes: "" });
  const [items, setItems] = useState([]);
  const [supplementInput, setSupplementInput] = useState("");
  const [mealInput, setMealInput] = useState({ name: "", time: "" });

  const load = async () => {
    const r = await api.get("/health/nutrition?days=14");
    setItems(r.data);
    const t = r.data.find((x) => x.log_date === date);
    if (t) setEntry({
      water_glasses: t.water_glasses || 0,
      supplements_taken: t.supplements_taken || [],
      meals: t.meals || [],
      weight_kg: t.weight_kg || "",
      notes: t.notes || "",
    });
    else setEntry({ water_glasses: 0, supplements_taken: [], meals: [], weight_kg: "", notes: "" });
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [date]);

  const save = async () => {
    try {
      await api.post("/health/nutrition", {
        log_date: date,
        water_glasses: Number(entry.water_glasses) || 0,
        supplements_taken: entry.supplements_taken,
        meals: entry.meals,
        weight_kg: entry.weight_kg ? Number(entry.weight_kg) : null,
        notes: entry.notes,
      });
      toast.success("Saved.");
      load();
    } catch { toast.error("Could not save."); }
  };

  const addWater = (n = 1) => setEntry({ ...entry, water_glasses: Math.max(0, (entry.water_glasses || 0) + n) });
  const addSupplement = () => { if (supplementInput.trim()) { setEntry({ ...entry, supplements_taken: [...(entry.supplements_taken || []), supplementInput.trim()] }); setSupplementInput(""); } };
  const removeSupplement = (i) => setEntry({ ...entry, supplements_taken: entry.supplements_taken.filter((_, idx) => idx !== i) });
  const addMeal = () => { if (mealInput.name.trim()) { setEntry({ ...entry, meals: [...(entry.meals || []), { ...mealInput }] }); setMealInput({ name: "", time: "" }); } };
  const removeMeal = (i) => setEntry({ ...entry, meals: entry.meals.filter((_, idx) => idx !== i) });

  return (
    <div className="space-y-8" data-testid="nutrition-page">
      <header className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <p className="overline text-saffron mb-2">Nutrition</p>
          <h1 className="font-serif font-semibold text-4xl sm:text-5xl">Today's nourishment</h1>
          <p className="text-ink-700 mt-2">Small, consistent choices become a healthy pregnancy.</p>
        </div>
        <input type="date" value={date} onChange={(e) => setDate(e.target.value)} className="input-line max-w-xs" data-testid="nutrition-date" />
      </header>

      <div className="grid lg:grid-cols-3 gap-6">
        <div className="vansha-card p-6" data-testid="water-card">
          <Drop weight="thin" size={28} className="text-terracotta mb-3" />
          <h3 className="font-serif text-xl">Hydration</h3>
          <p className="text-sm text-ink-700 mt-1 mb-4">Target: 8-10 glasses</p>
          <div className="flex items-center justify-between">
            <span className="font-serif font-semibold text-5xl text-terracotta" data-testid="water-count">{entry.water_glasses || 0}</span>
            <div className="flex gap-2">
              <button onClick={() => addWater(-1)} className="btn-ghost p-2 w-10 h-10" data-testid="water-minus">−</button>
              <button onClick={() => addWater(1)} className="btn-primary w-10 h-10 p-0" data-testid="water-plus">+</button>
            </div>
          </div>
          <div className="mt-4 grid grid-cols-10 gap-1">
            {[...Array(10)].map((_, i) => (
              <div key={i} className={`h-2 rounded-full ${i < (entry.water_glasses || 0) ? "bg-terracotta" : "bg-vansha-accent"}`} />
            ))}
          </div>
        </div>

        <div className="vansha-card p-6 lg:col-span-2" data-testid="supplements-card">
          <Plant weight="thin" size={28} className="text-forest mb-3" />
          <h3 className="font-serif text-xl">Supplements</h3>
          <p className="text-sm text-ink-700 mt-1 mb-4">Tick what you've taken today.</p>
          <div className="flex gap-2 mb-4">
            <input value={supplementInput} onChange={(e) => setSupplementInput(e.target.value)} onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), addSupplement())} className="input-line flex-1" placeholder="Folic acid, Iron, Vit D..." data-testid="supplement-input" />
            <button onClick={addSupplement} className="btn-secondary" data-testid="add-supplement">Add</button>
          </div>
          <div className="flex flex-wrap gap-2">
            {(entry.supplements_taken || []).map((s, i) => (
              <button key={i} onClick={() => removeSupplement(i)} className="text-sm px-3 py-1 rounded-full bg-forest text-white" data-testid={`supplement-${i}`}>{s} ×</button>
            ))}
          </div>
        </div>
      </div>

      <div className="vansha-card p-6" data-testid="meals-card">
        <h3 className="font-serif text-xl mb-4">Meals</h3>
        <div className="flex gap-2 mb-4 flex-wrap">
          <input value={mealInput.name} onChange={(e) => setMealInput({ ...mealInput, name: e.target.value })} className="input-line flex-1 min-w-[200px]" placeholder="Dal, rice, sabzi…" data-testid="meal-name" />
          <input value={mealInput.time} onChange={(e) => setMealInput({ ...mealInput, time: e.target.value })} className="input-line w-32" placeholder="13:00" data-testid="meal-time" />
          <button onClick={addMeal} className="btn-secondary" data-testid="add-meal">Add meal</button>
        </div>
        {(entry.meals || []).length > 0 && (
          <ul className="space-y-2">
            {entry.meals.map((m, i) => (
              <li key={i} className="flex items-center justify-between p-3 rounded-xl bg-vansha-accent text-sm" data-testid={`meal-${i}`}>
                <span><b>{m.time || "—"}</b> · {m.name}</span>
                <button onClick={() => removeMeal(i)} className="text-terracotta">Remove</button>
              </li>
            ))}
          </ul>
        )}
      </div>

      <div className="vansha-card p-6 grid sm:grid-cols-2 gap-5">
        <div><label className="overline mb-2 block">Weight today (kg)</label><input type="number" step="0.1" value={entry.weight_kg} onChange={(e) => setEntry({ ...entry, weight_kg: e.target.value })} className="input-line" data-testid="nut-weight" /></div>
        <div><label className="overline mb-2 block">Notes</label><input value={entry.notes} onChange={(e) => setEntry({ ...entry, notes: e.target.value })} className="input-line" data-testid="nut-notes" /></div>
      </div>

      <div className="flex justify-end">
        <button onClick={save} className="btn-primary" data-testid="nut-save">Save day</button>
      </div>
    </div>
  );
}
